import { http } from "@/lib/http";
import { isMockEnabled } from "@/lib/mock-flags";
import { setAgencyProfileMock } from "@/lib/mocks/agencyProfile";

/** Termo legal exibido no aceite (espelha legal_documents do agency-api). */
export interface OnboardingTerm {
  id: string;
  title: string;
  version: string;
  /** true = aceite obrigatório para continuar */
  required: boolean;
  /** corpo do termo em Markdown */
  body: string;
}

/** Item enviado ao backend ao registrar o aceite (auditoria LGPD). */
export interface AcceptedTerm {
  termId: string;
  version: string;
}

/**
 * Dados cadastrais da imobiliária preenchidos no onboarding. Usa o MESMO formato do
 * perfil em Configurações (settings) — o que é preenchido aqui alimenta aquela tela.
 */
export interface AgencyProfileInput {
  name: string;
  legalName: string;
  cnpj: string;
  email: string;
  phone: string;
  address: {
    street: string;
    number: string;
    neighborhood: string;
    city: string;
    state: string;
    zipCode: string;
  };
  social: {
    instagram?: string;
    website?: string;
  };
}

interface BackendLegalDocument {
  termId: string;
  version: string;
  title: string;
  bodyMarkdown: string;
  required: boolean;
  sortOrder: number;
}

// Espelha o OnboardingTermsStub do agency-api (mesmos term_id/version/Markdown) pra
// que o modo mock fique idêntico ao real. Fonte da verdade: GET /onboarding/terms.
const MOCK_TERMS: OnboardingTerm[] = [
  {
    id: "terms_of_use",
    version: "2026-06",
    required: true,
    title: "Termos de Uso",
    body: `## 1. Aceitação
Ao cadastrar sua imobiliária e utilizar o **ImobGo Agency**, você concorda com estes Termos de Uso.

## 2. A plataforma
O Portal da Imobiliária permite gerir licenças de corretores vinculados à sua organização, acompanhar a produção da equipe e administrar a conta da imobiliária.

## 3. Sua conta
Você é responsável pela confidencialidade das credenciais e pela veracidade dos dados cadastrais da imobiliária.`,
  },
  {
    id: "privacy_policy",
    version: "2026-06",
    required: true,
    title: "Política de Privacidade",
    body: `## 1. Quem somos
O **ImobGo** atua como controlador dos dados pessoais tratados na plataforma.

## 2. Dados que coletamos
Dados cadastrais da imobiliária (razão social, CNPJ, endereço) e dos corretores vinculados, além de registros de uso do portal.`,
  },
  {
    id: "lgpd_consent",
    version: "2026-06",
    required: true,
    title: "Consentimento para Tratamento de Dados (LGPD)",
    body: `## Tratamento conforme a LGPD
Este consentimento observa a **Lei nº 13.709/2018 (LGPD)**. Como titular, você pode acessar, corrigir, portar e solicitar a eliminação dos seus dados pelos canais de suporte.`,
  },
];

export async function getTerms(): Promise<OnboardingTerm[]> {
  if (isMockEnabled("agency")) {
    await new Promise((r) => setTimeout(r, 300));
    return MOCK_TERMS;
  }
  const { data } = await http.get<BackendLegalDocument[]>("/onboarding/terms");
  return data
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((d) => ({
      id: d.termId,
      title: d.title,
      version: d.version,
      required: d.required,
      body: d.bodyMarkdown,
    }));
}

export async function completeAgencyProfile(input: AgencyProfileInput): Promise<void> {
  if (isMockEnabled("agency")) {
    await new Promise((r) => setTimeout(r, 600));
    // Grava no store compartilhado pra que o perfil apareça em Configurações.
    setAgencyProfileMock(input);
    return;
  }
  await http.post("/auth/complete-profile", input);
}

export async function acceptTerms(terms: AcceptedTerm[]): Promise<void> {
  if (isMockEnabled("agency")) {
    await new Promise((r) => setTimeout(r, 400));
    return;
  }
  await http.post("/auth/accept-terms", { terms });
}
